import React, { useState } from 'react';
import './MobileLogin.css';
import { Eye, EyeOff, X } from 'lucide-react';

interface MobileLoginProps {
    onSubmit: (email: string, password: string) => void;
    onClose: () => void;
    loading: boolean;
}

const MobileLogin: React.FC<MobileLoginProps> = ({ onSubmit, onClose, loading }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const totalSteps = 2;
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [passwordVisible, setPasswordVisible] = useState(false);

    const progress = (currentStep / totalSteps) * 100;

    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        switch (step) {
            case 1:
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!email.trim()) {
                    newErrors.email = 'Email is required';
                } else if (!emailRegex.test(email)) {
                    newErrors.email = 'Please enter a valid email address';
                }
                break;
            case 2:
                if (!password) {
                    newErrors.password = 'Password is required';
                }
                break;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleNext = () => {
        if (validateStep(currentStep)) {
            if (currentStep === totalSteps) {
                onSubmit(email, password);
            } else {
                setCurrentStep(currentStep + 1);
            }
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            setErrors({});
        }
    };

    const handleClose = () => {
        if (currentStep > 1) {
            const confirmed = window.confirm('Are you sure you want to exit?');
            if (confirmed) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <div className="mobile-login-step">
                        <h2 className="mobile-login-step-title">What's your email?</h2>
                        <input
                            type="email"
                            className="mobile-login-input"
                            placeholder="Email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoFocus
                        />
                        {errors.email && <p className="mobile-login-error">{errors.email}</p>}
                    </div>
                );

            case 2:
                return (
                    <div className="mobile-login-step">
                        <h2 className="mobile-login-step-title">Enter your password</h2>
                        <div className="mobile-login-password-wrapper">
                            <input
                                type={passwordVisible ? "text" : "password"}
                                className="mobile-login-input"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoFocus
                            />
                            <button
                                type="button"
                                className="mobile-login-toggle-password"
                                onClick={() => setPasswordVisible(!passwordVisible)}
                            >
                                {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>
                        {errors.password && <p className="mobile-login-error">{errors.password}</p>}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="mobile-login-container">
            <div className="mobile-login-progress-bar" style={{ width: `${progress}%` }}></div>

            <button className="mobile-login-close-btn" onClick={handleClose}>
                <X size={24} />
            </button>

            {currentStep > 1 && (
                <button className="mobile-login-back-btn" onClick={handleBack}>
                    ← Back
                </button>
            )}

            <div className="mobile-login-step-container" key={currentStep}>
                {renderStep()}
            </div>

            <button
                className={`mobile-login-next-btn ${loading ? 'loading' : ''}`}
                onClick={handleNext}
                disabled={loading}
            >
                {loading ? 'Logging in...' : currentStep === totalSteps ? 'Login' : 'Next'}
            </button>
        </div>
    );
};

export default MobileLogin;
